import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge, Input, Select } from '@components';
import { WalletTransaction, BillingFilters } from '../types';

interface TransactionHistoryProps {
  transactions: WalletTransaction[];
  loading?: boolean;
  filters: BillingFilters;
  onFiltersChange: (filters: BillingFilters) => void;
  onExport?: () => void;
  pagination?: {
    count: number;
    next: string | null;
    previous: string | null;
    onNext?: () => void;
    onPrevious?: () => void;
  };
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  loading = false,
  filters,
  onFiltersChange,
  onExport,
  pagination,
}) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(filters.search || '');

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onFiltersChange({ ...filters, search: value });
  };

  const handleDateRangeChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    onFiltersChange({ ...filters, dateRange: stringValue as '' | '30' | '90' | '365' });
  };

  const getTransactionTypeBadge = (type: WalletTransaction['transaction_type']) => {
    const badges = {
      top_up: { variant: 'success' as const, label: t('billing.topUp') },
      charge: { variant: 'error' as const, label: t('billing.charge') },
      refund: { variant: 'info' as const, label: t('billing.refund') },
      adjustment: { variant: 'warning' as const, label: t('billing.adjustment') },
    };
    const badge = badges[type];
    return (
      <Badge variant={badge.variant} size="sm">
        {badge.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: string, type: WalletTransaction['transaction_type']) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return <span className="font-semibold text-gray-600 dark:text-gray-400">€0.00</span>;
    }
    const sign = type === 'top_up' || type === 'refund' ? '+' : '-';
    const color =
      type === 'top_up' || type === 'refund'
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
    return (
      <span className={`font-semibold ${color}`}>
        {sign}€{Math.abs(numAmount).toFixed(2)}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('billing.transactionHistory')}
        </h2>
        <div className="flex gap-2">
          {onExport && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onExport}
              className="flex items-center gap-2"
            >
              <span className="material-icons">download</span>
              {t('billing.export')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder={t('billing.searchTransactions')}
          value={searchTerm}
          onChange={e => handleSearchChange(e.target.value)}
          className="flex-1 px-4 py-2 text-base h-[40px]"
          size="md"
        />
        <Select
          value={filters.dateRange || ''}
          onChange={handleDateRangeChange}
          options={[
            { value: '', label: t('billing.allTransactions') },
            { value: '30', label: t('billing.last30Days') },
            { value: '90', label: t('billing.last90Days') },
            { value: '365', label: t('billing.lastMonth') },
          ]}
          className="w-52"
        />
      </div>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <span className="material-icons animate-spin text-4xl mb-2">refresh</span>
              <p>Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <span className="material-icons text-4xl mb-2">receipt_long</span>
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                      {t('common.date')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                      {t('common.type')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                      {t('common.description')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                      {t('common.amount')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                      {t('billing.balanceAfter')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr
                      key={transaction.id}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {formatDate(transaction.created)}
                      </td>
                      <td className="py-3 px-4">
                        {getTransactionTypeBadge(transaction.transaction_type)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {i18n.language === 'de' && transaction.description_de
                          ? transaction.description_de
                          : transaction.description}
                      </td>
                      <td className="py-3 px-4">
                        {formatAmount(transaction.amount, transaction.transaction_type)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        €
                        {(() => {
                          const parsed = parseFloat(transaction.balance_after);
                          return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination - Only show if there are multiple pages */}
          {pagination && (pagination.next || pagination.previous) && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {transactions.length} of {pagination.count} transactions
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={pagination.onPrevious}
                  disabled={!pagination.previous || loading}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={pagination.onNext}
                  disabled={!pagination.next || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
